import { gql, useQuery } from "@apollo/client";
import { CPagination } from "@coreui/react";
import {
  Badge,
  Button,
  ExportIcon,
  FilterIcon,
  Menu,
  Popover,
  Position,
  Spinner,
  Table,
  TextDropdownButton,
  toaster,
} from "evergreen-ui";
import React, { useEffect, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import AdminShipmentRow from "src/Components/AdminShipmentRow";
import Refresh from "src/Components/Refresh";
import { SHIPMENT_ORDERS_FRAGMENTS } from "src/fragments";
import useInput from "src/Hooks/useInput";
import { exportToCsv, getDateWithTime } from "src/utilities";
import styled from "styled-components";

export const LIST_SHIPMENT_ORDERS = gql`
  query ordersWithDelivery(
    $where: orders_with_delivery_bool_exp
    $limit: Int
    $offset: Int
  ) {
    orders_with_delivery(where: $where, order_by: { created_at: desc }) {
      ...ShipmentOrdersPart
    }
    paginated: orders_with_delivery(
      where: $where
      offset: $offset
      limit: $limit
      order_by: { created_at: desc }
    ) {
      ...ShipmentOrdersPart
    }
  }
  ${SHIPMENT_ORDERS_FRAGMENTS}
`;

const LoadingContainer = styled.div`
  ${(props) => props.theme.loadingContainer}
`;
const Container = styled.div`
  ${(props) => props.theme.pageContainer}
`;

const Title = styled.h1`
  ${(props) => props.theme.title}
`;

interface IProps extends RouteComponentProps<any> {}
const ShipmentOrders: React.SFC<IProps> = ({ match, history, location }) => {
  const columnOptions = [
    {
      label: "ID",
      value: "id",
    },
    {
      label: "Type",
      value: "type",
    },
    {
      label: "Create Date",
      value: "create_date",
    },
    {
      label: "Total Price",
      value: "total_price",
    },
    {
      label: "Payment",
      value: "payment",
    },
    {
      label: "Recipient",
      value: "recipient",
    },
    {
      label: "Full Address",
      value: "full_address",
    },
    {
      label: "Postcode",
      value: "postcode",
    },
    {
      label: "Remark",
      value: "remark",
    },
  ];

  const statusOptions = [
    {
      label: "All",
      value: "all",
    },
    {
      label: "Approved",
      value: "Approved",
    },
    {
      label: "Pending",
      value: "Pending",
    },
    {
      label: "Rejected",
      value: "Rejected",
    },
    {
      label: "Cancelled",
      value: "Cancelled",
    },
  ];

  const shippedOptions = [
    {
      label: "All",
      value: "all",
    },
    {
      label: "Shipped",
      value: "shipped",
    },
    {
      label: "Not Shipped",
      value: "not_shipped",
    },
  ];
  const checkType = () => {
    switch (match.params.order_type?.toLowerCase()) {
      case "mms":
        return "MMS";
      case "retail":
        return "Retail";
      case "rb":
        return "RB";
      case "drb":
        return "DRB";
      case "request_shipment":
        return "Request Shipment";
      default:
        return null;
    }
  };

  const customFilterType = (filter) => {
    switch (filter) {
      case "Request Shipment":
        return "normal";
      default:
        return filter?.toLowerCase();
    }
  };
  let limit = 10;

  let currentPage = +location.search.split("?page=")[1] || 1;
  const currentRoute = window.location.pathname;
  if (+location.search.split("?page=")[1] <= 0) {
    history.push(currentRoute + "?page=1");
  }
  const [page, setPage] = useState(currentPage);
  const searchValue = useInput("");
  let searchQuery = `${searchValue.value}`;
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [showColumn, setShowColumn] = useState("payment");
  const [isLoading, setIsLoading] = useState(false);
  const [shipped, setShipped] = useState(shippedOptions[0].value);
  let variables = {
    offset: limit * (+currentPage - 1) < 0 ? 1 : limit * (+currentPage - 1),
    limit,
    where: {
      _and: [
        customFilterType(checkType())
          ? {
              type: {
                _eq: customFilterType(checkType()),
              },
            }
          : {},
        selectedStatus === "all"
          ? {}
          : {
              status: {
                _eq: selectedStatus,
              },
            },
        shipped === "all"
          ? {}
          : {
              shipped: {
                _eq: shipped === "shipped",
              },
            },
        {
          id:
            isNaN(searchValue.value) || searchValue.value === ""
              ? {
                  _is_null: false,
                }
              : {
                  _eq: parseInt(searchValue.value),
                },
        },
        {
          _or: [
            {
              multiproduct: {
                _ilike: "%" + searchQuery + "%",
              },
            },
            {
              products: {
                name: {
                  _ilike: "%" + searchQuery + "%",
                },
              },
            },
            {
              recipient: {
                _ilike: searchQuery,
              },
            },
            {
              id: isNaN(searchValue.value)
                ? {
                    _eq: 0,
                  }
                : {
                    _eq: parseInt(searchValue.value),
                  },
            },
          ],
        },
      ],
    },
  };

  const { data, loading, error, refetch } = useQuery(LIST_SHIPMENT_ORDERS, {
    variables,
  });

  const refresh = () => {
    setIsLoading(true);
    refetch().then(() => {
      setIsLoading(false);
    });
  };

  const exportData = (e) => {
    e.preventDefault();
    let csv =
      "ID,AGENT USERNAME,TYPE,TOTAL QUANTITY,UNIT PRICE,DELIVERY RATE,PRODUCTS,CREATE DATE,STATUS,PAYMENT SLIP,RECIPIENT,ADDRESS,CITY,STATE,POSTCODE,COUNTRY,SHIPPED,TRACKING ID,TRACKING LINK,COURIER\n";

    data.orders_with_delivery.map((rec) => {
      return (csv =
        csv +
        `${rec.id},${rec.user ? rec.user.name : "N/A"} (${
          rec.user?.username
        }),${rec.type === "normal" ? "Request Shipping" : rec.type},${
          rec.quantity
        },${rec.unit_price},${rec.delivery_rate},"${
          +rec.products
            ? rec.products.name + " x" + rec.quantity
            : rec.multiproduct
            ? JSON.parse(rec.multiproduct).map(
                (pro) => pro.name + " x" + pro.quantity + " | "
              )
            : "N/A"
        }",${getDateWithTime(rec.created_at)},${rec.status},${
          rec.payment_slip || "NOT PAID"
        },${rec.recipient},${'"' + rec.address + '"'},${rec.city},${
          rec.state
        },${rec.postcode},${rec.name},${rec.shipped ? "YES" : "NO"},${
          rec.tracking_id
        },${rec.tracking_link},${rec.courier}\n`);
    });

    exportToCsv(csv, "Shipment_orders");
  };

  const pageChange = (newPage) => {
    currentPage !== newPage && history.push(`${currentRoute}?page=${newPage}`);
  };

  useEffect(() => {
    currentPage !== page && setPage(currentPage);
    if (error) {
      toaster.danger(error.message);
    }
  }, [error, currentPage, page]);

  return (
    <Container>
      <div className=" admin-page-header">
        <Title className=" flex flex-row items-center">
          Shipment Orders ({data?.orders_with_delivery?.length}){" "}
          <span>
            {checkType() && <Badge color="yellow">{checkType()}</Badge>}
          </span>
          <span>
            <Refresh onClick={refresh} loading={isLoading} />
          </span>
        </Title>
        <div className=" admin-buttons-group">
          {/* <Button
            onClick={() => {}}
            appearance={"minimal"}
            iconBefore={AddIcon}
          >
            New Order
          </Button> */}
          <Button
            onClick={() => {
              toaster.warning(
                "Coming soon! You may export the data, and use Excel table format tool for now."
              );
            }}
            appearance={"minimal"}
            iconBefore={FilterIcon}
          >
            Advanced Filter
          </Button>
          <Button
            onClick={exportData}
            appearance={"minimal"}
            iconBefore={ExportIcon}
          >
            Export to Excel
          </Button>
        </div>
      </div>
      <Table
        marginBottom={"1rem"}
        maxHeight={"90vh"}
        width="100%"
        maxWidth="calc(100vw - 2rem)"
        overflow="scroll"
      >
        <Table.Head minWidth="1080px" accountForScrollbar={true}>
          <Table.SearchHeaderCell
            flexBasis={200}
            flexShrink={0}
            flexGrow={0}
            {...searchValue}
            className=" w-80"
            placeholder="Search ID, Agent Name.."
          />
          <Table.TextHeaderCell>QUANTITY</Table.TextHeaderCell>
          <Table.TextHeaderCell>PRODUCTS</Table.TextHeaderCell>
          <Table.TextHeaderCell minWidth={"140px"}>
            <Popover
              position={Position.BOTTOM_LEFT}
              content={({ close }) => (
                <Menu>
                  <Menu.OptionsGroup
                    title="Show"
                    options={columnOptions}
                    selected={showColumn}
                    onChange={(value) => {
                      setShowColumn(value);
                      close();
                    }}
                  />
                </Menu>
              )}
            >
              <TextDropdownButton>
                {showColumn.replace("_", " ").toUpperCase()}
              </TextDropdownButton>
            </Popover>
          </Table.TextHeaderCell>
          <Table.TextHeaderCell>
            <Popover
              position={Position.BOTTOM_LEFT}
              content={({ close }) => (
                <Menu>
                  <Menu.OptionsGroup
                    title="Status"
                    options={statusOptions}
                    selected={selectedStatus}
                    onChange={(value) => {
                      setSelectedStatus(value);
                    }}
                  />
                </Menu>
              )}
            >
              <TextDropdownButton>
                {selectedStatus === "all"
                  ? "STATUS"
                  : selectedStatus.toUpperCase()}
              </TextDropdownButton>
            </Popover>
          </Table.TextHeaderCell>
          <Table.TextHeaderCell>
            <Popover
              position={Position.BOTTOM_LEFT}
              content={({ close }) => (
                <Menu>
                  <Menu.OptionsGroup
                    title="Show"
                    options={shippedOptions}
                    selected={shipped}
                    onChange={(value) => {
                      setShipped(value);
                      close();
                    }}
                  />
                </Menu>
              )}
            >
              <TextDropdownButton>
                {shipped.replace("_", " ").toUpperCase()}
              </TextDropdownButton>
            </Popover>
          </Table.TextHeaderCell>
          <Table.TextHeaderCell />
        </Table.Head>
        <Table.Body
          marginBottom={"1rem"}
          maxHeight={"90vh"}
          minWidth="1080px"
          overflow="scroll"
        >
          {loading && (
            <LoadingContainer>
              <Spinner />
            </LoadingContainer>
          )}
          {!loading && error && error.message}
          {!loading &&
            data &&
            data.paginated &&
            (data.paginated.length > 0
              ? data.paginated.map((result, index) => {
                  return (
                    <AdminShipmentRow
                      key={result.type + result.id}
                      record={result}
                      index={index}
                      variables={variables}
                      showColumn={showColumn}
                    />
                  );
                })
              : "No record found.")}
        </Table.Body>
      </Table>
      {!loading && data && data.orders_with_delivery && (
        <CPagination
          pages={Math.ceil(data.orders_with_delivery.length / limit)}
          activePage={parseInt(page.toString())}
          limit={5}
          dots={true}
          onActivePageChange={pageChange}
        />
      )}
    </Container>
  );
};

export default withRouter(ShipmentOrders);
